import { useState, useEffect, useCallback } from 'react';
import type { CorrectionRecord, PracticeRecord } from '../components/training/TrainingDashboard';
import type { ConfidenceStats, KnowledgeEntry } from '../types/foreman';

interface ConfidenceHistoryPoint {
  date: string;
  corrections: number;
  confidence: number;
}

export interface ProductRecord {
  id: number;
  sku: string;
  name: string;
  brand: string;
  category: string;
  subcategory: string;
  price: number;
  vendor: string;
  vendorSku: string;
  leadTimeDays: number;
  inStock: boolean;
  unit: string;
  notes: string;
}

export interface WasteFactorRecord {
  id: number;
  materialType: string;
  pattern: string;
  factor: number;
  notes: string;
}

interface UseTrainingResult {
  corrections: CorrectionRecord[];
  practices: PracticeRecord[];
  products: ProductRecord[];
  wasteFactors: WasteFactorRecord[];
  stats: ConfidenceStats | null;
  knowledgeFeed: KnowledgeEntry[];
  confidenceHistory: ConfidenceHistoryPoint[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  // Practice mutations
  createPractice: (data: Record<string, unknown>) => Promise<void>;
  updatePractice: (id: string | number, data: Record<string, unknown>) => Promise<void>;
  deletePractice: (id: string | number) => Promise<void>;
  // Product mutations
  createProduct: (data: Record<string, unknown>) => Promise<void>;
  updateProduct: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteProduct: (id: number) => Promise<void>;
  // Waste factor mutations
  createWasteFactor: (data: Record<string, unknown>) => Promise<void>;
  updateWasteFactor: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteWasteFactor: (id: number) => Promise<void>;
  // Bulk mutations
  bulkCreatePractices: (records: Record<string, unknown>[]) => Promise<{ inserted: number }>;
  bulkCreateProducts: (records: Record<string, unknown>[]) => Promise<{ inserted: number }>;
  bulkCreateWasteFactors: (records: Record<string, unknown>[]) => Promise<{ inserted: number }>;
  // Correction mutations
  resolveCorrection: (id: number, resolved: boolean) => Promise<void>;
  deleteCorrection: (id: number) => Promise<void>;
}

const TRAINING_BASE = '/api/training';
const TOKEN_KEY = 'foreman-auth-token';

async function fetchWithAuth<T>(path: string): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${TRAINING_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

async function mutateWithAuth(path: string, method: string, body?: unknown): Promise<unknown> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${TRAINING_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export function useTraining(): UseTrainingResult {
  const [corrections, setCorrections] = useState<CorrectionRecord[]>([]);
  const [practices, setPractices] = useState<PracticeRecord[]>([]);
  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [wasteFactors, setWasteFactors] = useState<WasteFactorRecord[]>([]);
  const [stats, setStats] = useState<ConfidenceStats | null>(null);
  const [knowledgeFeed, setKnowledgeFeed] = useState<KnowledgeEntry[]>([]);
  const [confidenceHistory, setConfidenceHistory] = useState<ConfidenceHistoryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    Promise.all([
      fetchWithAuth<{ corrections: CorrectionRecord[]; total: number }>('/corrections?limit=200'),
      fetchWithAuth<{ practices: PracticeRecord[]; total: number }>('/practices?limit=200'),
      fetchWithAuth<ConfidenceStats>('/stats'),
      fetchWithAuth<{ entries: KnowledgeEntry[] }>('/knowledge'),
      fetchWithAuth<{ history: ConfidenceHistoryPoint[] }>('/confidence-history?days=90'),
      fetchWithAuth<{ products: ProductRecord[]; total: number }>('/products?limit=500'),
      fetchWithAuth<{ wasteFactors: WasteFactorRecord[]; total: number }>('/waste-factors'),
    ])
      .then(([corr, prac, st, know, hist, prod, wf]) => {
        setCorrections(corr.corrections);
        setPractices(prac.practices);
        setStats(st);
        setKnowledgeFeed(know.entries);
        setConfidenceHistory(hist.history);
        setProducts(prod.products);
        setWasteFactors(wf.wasteFactors);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Practice mutations
  const createPractice = useCallback(async (data: Record<string, unknown>) => {
    await mutateWithAuth('/practices', 'POST', data);
    refresh();
  }, [refresh]);

  const updatePractice = useCallback(async (id: string | number, data: Record<string, unknown>) => {
    await mutateWithAuth(`/practices/${id}`, 'PUT', data);
    refresh();
  }, [refresh]);

  const deletePractice = useCallback(async (id: string | number) => {
    await mutateWithAuth(`/practices/${id}`, 'DELETE');
    refresh();
  }, [refresh]);

  // Product mutations
  const createProduct = useCallback(async (data: Record<string, unknown>) => {
    await mutateWithAuth('/products', 'POST', data);
    refresh();
  }, [refresh]);

  const updateProduct = useCallback(async (id: number, data: Record<string, unknown>) => {
    await mutateWithAuth(`/products/${id}`, 'PUT', data);
    refresh();
  }, [refresh]);

  const deleteProduct = useCallback(async (id: number) => {
    await mutateWithAuth(`/products/${id}`, 'DELETE');
    refresh();
  }, [refresh]);

  // Waste factor mutations
  const createWasteFactor = useCallback(async (data: Record<string, unknown>) => {
    await mutateWithAuth('/waste-factors', 'POST', data);
    refresh();
  }, [refresh]);

  const updateWasteFactor = useCallback(async (id: number, data: Record<string, unknown>) => {
    await mutateWithAuth(`/waste-factors/${id}`, 'PUT', data);
    refresh();
  }, [refresh]);

  const deleteWasteFactor = useCallback(async (id: number) => {
    await mutateWithAuth(`/waste-factors/${id}`, 'DELETE');
    refresh();
  }, [refresh]);

  // Bulk mutations
  const bulkCreatePractices = useCallback(async (records: Record<string, unknown>[]) => {
    const result = await mutateWithAuth('/practices/bulk', 'POST', { records }) as { inserted: number };
    refresh();
    return result;
  }, [refresh]);

  const bulkCreateProducts = useCallback(async (records: Record<string, unknown>[]) => {
    const result = await mutateWithAuth('/products/bulk', 'POST', { records }) as { inserted: number };
    refresh();
    return result;
  }, [refresh]);

  const bulkCreateWasteFactors = useCallback(async (records: Record<string, unknown>[]) => {
    const result = await mutateWithAuth('/waste-factors/bulk', 'POST', { records }) as { inserted: number };
    refresh();
    return result;
  }, [refresh]);

  // Correction mutations
  const resolveCorrection = useCallback(async (id: number, resolved: boolean) => {
    await mutateWithAuth(`/corrections/${id}`, 'PUT', { resolved });
    refresh();
  }, [refresh]);

  const deleteCorrection = useCallback(async (id: number) => {
    await mutateWithAuth(`/corrections/${id}`, 'DELETE');
    refresh();
  }, [refresh]);

  return {
    corrections,
    practices,
    products,
    wasteFactors,
    stats,
    knowledgeFeed,
    confidenceHistory,
    isLoading,
    error,
    refresh,
    createPractice,
    updatePractice,
    deletePractice,
    createProduct,
    updateProduct,
    deleteProduct,
    createWasteFactor,
    updateWasteFactor,
    deleteWasteFactor,
    bulkCreatePractices,
    bulkCreateProducts,
    bulkCreateWasteFactors,
    resolveCorrection,
    deleteCorrection,
  };
}
